/**
 * Der Abschnitt "Flug-Sendung" (`ship-sendung` im Modus "air") - Gegenstueck zu `DsvSendung.tsx`.
 * Anders als bei DSV gibt es kein Suchfeld und keine Verknuepfung: alle drei Felder sind direkte
 * Handeingaben, jede einzeln per `onAendern` geschrieben (Spec E1/E2/E4, `docs/superpowers/specs/
 * 2026-08-26-spedition-see-flug-design.md`). Bauplan wie `Referenzen.tsx`/`Transport.tsx`: lokales
 * `patch(feld, wert)` aus `bestellung`/`onAendern`, keine eigene Aufrufform.
 */
import type { Order } from "../../../lib/types.ts";

export interface AirStatusOption {
  id: string;
  label: string;
}

/**
 * Die drei manuellen Zustaende einer Flug-Sendung (Spec 2026-08-26-spedition-see-flug-design.md,
 * Abschnitt 8: "Vorschlag dieses Entwurfs, nicht woertlich vom Auftraggeber bestaetigt"). Anders
 * als SHIP_STAGES (config/shipping.ts) gibt es hier keine abgeleitete Reihenfolge - der
 * Sachbearbeiter setzt den Wert direkt. Bewusst HIER statt in config/shipping.ts: diese Datei ist
 * eine 1:1-Portierung aus index.html (siehe ihr eigener Kopfkommentar) und wird von
 * test/reference/herkunft.test.ts auf eine Herkunftsmarke je Wert-Export geprueft - ein neu
 * erfundener Wert wie dieser hat keine und macht diesen Test deterministisch rot. Task 8
 * (Stationen.tsx) importiert AIR_STATUS_OPTIONEN von hier.
 */
export const AIR_STATUS_OPTIONEN: readonly AirStatusOption[] = [
  { id: "gebucht", label: "Gebucht" },
  { id: "unterwegs", label: "Unterwegs" },
  { id: "angekommen", label: "Angekommen" },
];

export interface FlugSendungProps {
  bestellung: Order;
  onAendern: (id: string, aenderung: Record<string, unknown>) => void;
}

export function FlugSendung({ bestellung: o, onAendern }: FlugSendungProps) {
  const id = o.id as string;
  const patch = (feld: string, wert: unknown): void => onAendern(id, { [feld]: wert });

  const airCarrier = o.airCarrier ?? "";
  const airAwbNo = o.airAwbNo ?? "";
  const airStatus = AIR_STATUS_OPTIONEN.some((s) => s.id === o.airStatus) ? o.airStatus! : "";

  return (
    <div className="field-grid">
      <div className="field">
        <label>Carrier / Airline</label>
        <input
          value={airCarrier}
          placeholder="z. B. Lufthansa Cargo"
          onChange={(e) => patch("airCarrier", e.currentTarget.value)}
        />
      </div>
      <div className="field">
        <label>AWB-Nr.</label>
        <input
          className="mono"
          value={airAwbNo}
          placeholder="z. B. 020-12345678"
          onChange={(e) => patch("airAwbNo", e.currentTarget.value)}
        />
      </div>
      <div className="field">
        <label>Status</label>
        <select value={airStatus} onChange={(e) => patch("airStatus", e.currentTarget.value)}>
          <option value="" disabled>
            Bitte wählen
          </option>
          {AIR_STATUS_OPTIONEN.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
